import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Response } from '../../../_shared/models';
import { PaginatedResult } from '../../../_shared/models/paginated-result.model';
import { LookupComplianceFrequency, LookupIncidentType, LookupMake, LookupModel, LookupPartCategory, LookupPartReturnReason, LookupPartType, LookupSupplier, LookupWarehouse, LookupWorkOrder, LookupWorkshop } from '../models/lookup.model';
import { MakeModel } from '../models/make-model.model';
import { UserAssetLookupItem, UserAssetLookupRequest } from '../models/user-asset-lookup.model';

@Injectable({ providedIn: 'root' })
export class LookupService {
  private http = inject(HttpClient);

  getMakesAsync(): Promise<Response<LookupMake[]>> {
    return firstValueFrom(
      this.http.get<Response<LookupMake[]>>(`${environment.apiEndpoint}/asset/lookup/makes`)
    );
  }

  getModelsByMakeIdAsync(makeId: number): Promise<Response<LookupModel[]>> {
    return firstValueFrom(
      this.http.get<Response<LookupModel[]>>(`${environment.apiEndpoint}/asset/lookup/models/${makeId}`)
    );
  }

  getAllModelsAsync(): Promise<Response<LookupModel[]>> {
    return firstValueFrom(
      this.http.get<Response<LookupModel[]>>(`${environment.apiEndpoint}/asset/lookup/models`)
    );
  }

  getPartCategoriesAsync(): Promise<Response<LookupPartCategory[]>> {
    return firstValueFrom(
      this.http.get<Response<LookupPartCategory[]>>(`${environment.apiEndpoint}/asset/lookup/parts-categories`)
    );
  }

  getPartTypesAsync(): Promise<Response<LookupPartType[]>> {
    return firstValueFrom(
      this.http.get<Response<LookupPartType[]>>(`${environment.apiEndpoint}/asset/lookup/parts-types`)
    );
  }

  getPartReturnReasonsAsync(cancellation?: Subject<void>): Promise<Response<LookupPartReturnReason[]>> {
    const request = this.http.get<Response<LookupPartReturnReason[]>>(`${environment.apiEndpoint}/asset/lookup/part-return-reasons`);

    return firstValueFrom(cancellation ? request.pipe(takeUntil(cancellation)) : request);
  }

  getWorkOrdersAsync(searchTerm: string, cancellation: Subject<void>): Promise<Response<LookupWorkOrder[]>> {
    const query = encodeURIComponent(searchTerm);

    return firstValueFrom(
      this.http.get<Response<LookupWorkOrder[]>>(`${environment.apiEndpoint}/asset/lookup/work-orders?searchTerm=${query}`)
        .pipe(takeUntil(cancellation))
    );
  }

  getWarehousesAsync(accountIds: number[] = []): Promise<Response<LookupWarehouse[]>> {
    return firstValueFrom(
      this.http.post<Response<LookupWarehouse[]>>(`${environment.apiEndpoint}/asset/lookup/warehouses`, { accountIds })
    );
  }

  getSuppliersAsync(accountIds: number[] = []): Promise<Response<LookupSupplier[]>> {
    const params = accountIds.reduce(
      (query, accountId) => query.append('accountIds', accountId.toString()),
      new HttpParams()
    );

    return firstValueFrom(
      this.http.get<Response<LookupSupplier[]>>(`${environment.apiEndpoint}/asset/lookup/suppliers`, { params })
    );
  }

  getWorkshopsAsync(accountIds: number[] = []): Promise<Response<LookupWorkshop[]>> {
    return firstValueFrom(
      this.http.post<Response<LookupWorkshop[]>>(`${environment.apiEndpoint}/asset/lookup/workshops`, { accountIds })
    );
  }

  getWorkshopsByAccountHierarchyAsync(accountId: number, cancellation: Subject<void>): Promise<Response<LookupWorkshop[]>> {
    return firstValueFrom(
      this.http.get<Response<LookupWorkshop[]>>(`${environment.apiEndpoint}/asset/lookup/workshops/by-account-hierarchy/${accountId}`)
        .pipe(takeUntil(cancellation))
    );
  }

  getMakeModelsAsync(accountIds: number[] = [], cancellation: Subject<void>): Promise<Response<MakeModel[]>> {
    return firstValueFrom(
      this.http.post<Response<MakeModel[]>>(`${environment.apiEndpoint}/asset/lookup/make-models`, { accountIds })
        .pipe(takeUntil(cancellation))
    );
  }

  getUserAssetsAsync(request: UserAssetLookupRequest, cancellation: Subject<void>): Promise<Response<PaginatedResult<UserAssetLookupItem>>> {
    return firstValueFrom(
      this.http.post<Response<PaginatedResult<UserAssetLookupItem>>>(`${environment.apiEndpoint}/asset/lookup/user-assets`, request)
        .pipe(takeUntil(cancellation))
    );
  }

  getComplianceFrequenciesAsync(): Promise<Response<LookupComplianceFrequency[]>> {
    return firstValueFrom(
      this.http.get(`${environment.apiEndpoint}/asset/lookup/compliance-frequencies`)
    );
  }

  getIncidentTypesAsync(cancellation: Subject<void>): Promise<Response<LookupIncidentType[]>> {
    return firstValueFrom(
      this.http.get<Response<LookupIncidentType[]>>(`${environment.apiEndpoint}/asset/lookup/incident-types`)
        .pipe(takeUntil(cancellation))
    );
  }
}
